import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import logo from '../assets/jnan-logo.png';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openServiceMenu, setOpenServiceMenu] = useState(false);
  const [openMobileServiceMenu, setOpenMobileServiceMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Amazon Integration', path: '/amazon-integration' },
    { name: 'Product', path: '/product' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    { name: 'AI Integration', path: '/services#ai-integration' },
    { name: 'Web Development', path: '/services#web-development' },
    { name: 'App Development', path: '/services#app-development' },
    { name: 'Custom Integrations', path: '/services#integrations' },
    { name: 'Shopify Solutions', path: '/services#shopify' },
    { name: 'NetSuite Solutions', path: '/services#netsuite' },
    { name: 'ERP Solutions', path: '/services#erp-solutions' },
    { name: 'IT Services & Support', path: '/services#it-services' },
    { name: 'Cloud Infrastructure', path: '/services#cloud-infrastructure' },
    { name: 'Cybersecurity Solutions', path: '/services#cybersecurity' },
    { name: 'Amazon SP-API', path: '/amazon-integration' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ?
        'bg-white/80 backdrop-blur-xl shadow-lg shadow-slate-200/20 py-3' :
        'bg-transparent py-5'}`
        }>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              data-testid="navbar-logo"
              className="flex items-center gap-2 group">
              <img 
                src={logo} 
                alt="Logo" 
                className="w-24 h-12 object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                if (link.name === 'Services') {
                  return (
                    <div
                      key={link.name}
                      className="relative group"
                      onMouseEnter={() => setOpenServiceMenu(true)}
                      onMouseLeave={() => setOpenServiceMenu(false)}
                    >
                      <Link
                        to={link.path}
                        data-testid={`nav-link-${link.name.toLowerCase()}`}
                        className={`nav-link px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                          isActive(link.path)
                            ? 'text-blue-600'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                      </Link>

                      {/* Submenu */}
                      <div
                        className={`absolute left-0 mt-0 w-full min-w-max bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden transition-all duration-300 z-50 ${
                          openServiceMenu
                            ? 'opacity-100 visible translate-y-0'
                            : 'opacity-0 invisible -translate-y-2'
                        }`}
                        style={{ width: '440px' }}
                      >
                        <div className="py-3 px-3">
                          <div className="grid grid-cols-2 gap-0">
                            {services.map((service, index) => (
                              <Link
                                key={service.name}
                                to={service.path}
                                onClick={() => setOpenServiceMenu(false)}
                                className={`px-3 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors rounded ${
                                  index % 2 === 0 ? 'border-r border-slate-100' : ''
                                }`}
                              >
                                {service.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                    className={`nav-link px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-blue-600'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/contact">
                <Button
                  data-testid="navbar-cta-button"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full px-6 py-2 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all btn-shine">

                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>

              {isMobileMenuOpen ?
              <X className="w-6 h-6 text-slate-700" /> :

              <Menu className="w-6 h-6 text-slate-700" />
              }
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          data-testid="mobile-menu"
          className={`md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl shadow-xl transition-all duration-300 ${
          isMobileMenuOpen ?
          'opacity-100 translate-y-0' :
          'opacity-0 -translate-y-4 pointer-events-none'}`
          }>

          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => {
              if (link.name === 'Services') {
                return (
                  <div key={link.name}>
                    <button
                      data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                      onClick={() => setOpenMobileServiceMenu(!openMobileServiceMenu)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors flex items-center justify-between ${
                        isActive(link.path)
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {link.name}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          openMobileServiceMenu ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openMobileServiceMenu && (
                      <div className="bg-slate-50 rounded-xl mx-2 my-1 overflow-hidden p-3">
                        <div className="grid grid-cols-2 gap-2">
                          {services.map((service) => (
                            <Link
                              key={service.name}
                              to={service.path}
                              onClick={() => {
                                setOpenMobileServiceMenu(false);
                                setIsMobileMenuOpen(false);
                              }}
                              className="text-sm text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors p-2.5 rounded-lg border-l-2 border-transparent hover:border-blue-600"
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
            <div className="pt-4 px-4">
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  data-testid="mobile-cta-button"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full py-3 font-medium">

                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div className="h-20" />
    </>);

};

export default Navbar;